import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ListFilter, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { ServiceCategoryFilterOption } from "./topServicesListQuery";
import {
  countActiveTopServicesFilters,
  DEFAULT_TOP_SERVICES_FILTERS,
  formatTopServicesPriceRange,
  TOP_SERVICES_AVAILABILITY_OPTIONS,
  TOP_SERVICES_PRICE_MAX,
  TOP_SERVICES_PRICE_MIN,
  type TopServicesAvailabilityDay,
  type TopServicesFiltersState,
} from "./topServicesFilters";

const FILTER_CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const FILTER_SECTION_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-[#737373] outline-none transition-colors hover:bg-zinc-50/80 focus-visible:ring-0";

type FilterSectionId =
  | "serviceActivity"
  | "price"
  | "category"
  | "availability";

interface AnalyticsServicesFilterPopoverProps {
  applied: TopServicesFiltersState;
  onApply: (next: TopServicesFiltersState) => void;
  categoryOptions: ServiceCategoryFilterOption[];
}

function isCategorySelected(
  categoryIds: string[],
  allCategoryIds: string[],
  id: string,
): boolean {
  if (allCategoryIds.length === 0) {
    return false;
  }

  if (categoryIds.length === 0) {
    return true;
  }

  return categoryIds.includes(id);
}

function matchesPanelSearch(label: string, query: string): boolean {
  if (!query.trim()) {
    return true;
  }

  return label.toLowerCase().includes(query.trim().toLowerCase());
}

type FilterSectionSearchLabels = {
  serviceActivity: string;
  activities: string;
  services: string;
  price: string;
  category: string;
  availability: string;
};

function getFilterSectionVisibility(
  panelQuery: string,
  labels: FilterSectionSearchLabels,
  visibleCategoryCount: number,
  visibleAvailabilityCount: number,
) {
  return {
    showServiceActivitySection:
      matchesPanelSearch(labels.serviceActivity, panelQuery) ||
      matchesPanelSearch(labels.activities, panelQuery) ||
      matchesPanelSearch(labels.services, panelQuery),
    showPriceSection: matchesPanelSearch(labels.price, panelQuery),
    showCategorySection:
      matchesPanelSearch(labels.category, panelQuery) ||
      visibleCategoryCount > 0,
    showAvailabilitySection:
      matchesPanelSearch(labels.availability, panelQuery) ||
      visibleAvailabilityCount > 0,
  };
}

const FILTER_SECTION_PANEL_CLASS =
  "overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]";

function ExpandableFilterSection({
  title,
  sectionId,
  expandedSection,
  onToggleSection,
  children,
}: {
  title: string;
  sectionId: FilterSectionId;
  expandedSection: FilterSectionId | null;
  onToggleSection: (section: FilterSectionId) => void;
  children: React.ReactNode;
}) {
  const isExpanded = expandedSection === sectionId;

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="span"
        className={FILTER_SECTION_TRIGGER_CLASS}
        onClick={() => onToggleSection(sectionId)}
      >
        {title}
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-[#737373] transition-transform",
            isExpanded && "rotate-180",
          )}
        />
      </Button>
      {isExpanded ? children : null}
    </div>
  );
}

function ServiceActivityFilterSection({
  draft,
  setDraft,
  expandedSection,
  onToggleSection,
}: {
  draft: TopServicesFiltersState;
  setDraft: React.Dispatch<React.SetStateAction<TopServicesFiltersState>>;
  expandedSection: FilterSectionId | null;
  onToggleSection: (section: FilterSectionId) => void;
}) {
  const { t } = useTranslation();

  return (
    <ExpandableFilterSection
      title={t(
        "analytics.services.filters.serviceActivity",
        "Service/ Activity",
      )}
      sectionId="serviceActivity"
      expandedSection={expandedSection}
      onToggleSection={onToggleSection}
    >
      <div className={cn(FILTER_SECTION_PANEL_CLASS, "p-1")}>
        <FilterCheckboxRow
          checked={draft.serviceActivity.activities}
          label={t("analytics.services.filters.activities", "Activities")}
          onCheckedChange={() =>
            setDraft((previous) => ({
              ...previous,
              serviceActivity: {
                ...previous.serviceActivity,
                activities: !previous.serviceActivity.activities,
              },
            }))
          }
        />
        <FilterCheckboxRow
          checked={draft.serviceActivity.services}
          label={t("analytics.services.filters.services", "Services")}
          onCheckedChange={() =>
            setDraft((previous) => ({
              ...previous,
              serviceActivity: {
                ...previous.serviceActivity,
                services: !previous.serviceActivity.services,
              },
            }))
          }
        />
      </div>
    </ExpandableFilterSection>
  );
}

function PriceFilterSection({
  draft,
  setDraft,
  expandedSection,
  onToggleSection,
}: {
  draft: TopServicesFiltersState;
  setDraft: React.Dispatch<React.SetStateAction<TopServicesFiltersState>>;
  expandedSection: FilterSectionId | null;
  onToggleSection: (section: FilterSectionId) => void;
}) {
  const { t } = useTranslation();

  return (
    <ExpandableFilterSection
      title={t("analytics.services.filters.price", "Price")}
      sectionId="price"
      expandedSection={expandedSection}
      onToggleSection={onToggleSection}
    >
      <div className={cn(FILTER_SECTION_PANEL_CLASS, "px-3 py-2")}>
        <PriceRangeSlider
          value={draft.priceRange}
          onChange={(priceRange) =>
            setDraft((previous) => ({ ...previous, priceRange }))
          }
        />
      </div>
    </ExpandableFilterSection>
  );
}

function CategoryFilterSection({
  categorySearch,
  setCategorySearch,
  visibleCategoryOptions,
  toggleCategory,
  isCategoryChecked,
  expandedSection,
  onToggleSection,
}: {
  categorySearch: string;
  setCategorySearch: React.Dispatch<React.SetStateAction<string>>;
  visibleCategoryOptions: ServiceCategoryFilterOption[];
  toggleCategory: (id: string) => void;
  isCategoryChecked: (id: string) => boolean;
  expandedSection: FilterSectionId | null;
  onToggleSection: (section: FilterSectionId) => void;
}) {
  const { t } = useTranslation();

  return (
    <ExpandableFilterSection
      title={t("analytics.services.filters.category", "Category")}
      sectionId="category"
      expandedSection={expandedSection}
      onToggleSection={onToggleSection}
    >
      <div className={FILTER_SECTION_PANEL_CLASS}>
        <div className="border-b border-[#E5E5E5] p-2">
          <Input
            type="search"
            icon={<Search className="size-4 text-[#737373]" />}
            placeholder={t("analytics.common.search", "Search")}
            value={categorySearch}
            onChange={(event) => setCategorySearch(event.target.value)}
            className="h-8 border-[#E5E5E5] text-sm shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-[176px] overflow-y-auto p-1">
          {visibleCategoryOptions.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-[#737373]">
              {t("analytics.common.noCategoriesFound", "No categories found")}
            </p>
          ) : null}
          {visibleCategoryOptions.map((option) => (
            <FilterCheckboxRow
              key={option.id}
              checked={isCategoryChecked(option.id)}
              label={option.label}
              onCheckedChange={() => toggleCategory(option.id)}
            />
          ))}
        </div>
      </div>
    </ExpandableFilterSection>
  );
}

function AvailabilityFilterSection({
  draft,
  availabilitySearch,
  setAvailabilitySearch,
  visibleAvailabilityOptions,
  toggleAvailability,
  expandedSection,
  onToggleSection,
}: {
  draft: TopServicesFiltersState;
  availabilitySearch: string;
  setAvailabilitySearch: React.Dispatch<React.SetStateAction<string>>;
  visibleAvailabilityOptions: typeof TOP_SERVICES_AVAILABILITY_OPTIONS;
  toggleAvailability: (key: TopServicesAvailabilityDay) => void;
  expandedSection: FilterSectionId | null;
  onToggleSection: (section: FilterSectionId) => void;
}) {
  const { t } = useTranslation();

  return (
    <ExpandableFilterSection
      title={t("analytics.services.filters.availability", "Availability")}
      sectionId="availability"
      expandedSection={expandedSection}
      onToggleSection={onToggleSection}
    >
      <div className={FILTER_SECTION_PANEL_CLASS}>
        <div className="border-b border-[#E5E5E5] p-2">
          <Input
            type="search"
            icon={<Search className="size-4 text-[#737373]" />}
            placeholder={t("analytics.common.search", "Search")}
            value={availabilitySearch}
            onChange={(event) => setAvailabilitySearch(event.target.value)}
            className="h-8 border-[#E5E5E5] text-sm shadow-none focus-visible:ring-0"
          />
        </div>
        <div className="max-h-[220px] overflow-y-auto p-1">
          {visibleAvailabilityOptions.map((option) => (
            <FilterCheckboxRow
              key={option.key}
              checked={
                option.key === "all"
                  ? draft.availability.includes("all")
                  : draft.availability.includes(option.key)
              }
              label={t(`analytics.weekdays.${option.key}`, option.label)}
              onCheckedChange={() => toggleAvailability(option.key)}
            />
          ))}
        </div>
      </div>
    </ExpandableFilterSection>
  );
}

interface AnalyticsServicesFilterPanelProps {
  activeCount: number;
  draft: TopServicesFiltersState;
  setDraft: React.Dispatch<React.SetStateAction<TopServicesFiltersState>>;
  expandedSection: FilterSectionId | null;
  toggleSection: (section: FilterSectionId) => void;
  categorySearch: string;
  setCategorySearch: React.Dispatch<React.SetStateAction<string>>;
  availabilitySearch: string;
  setAvailabilitySearch: React.Dispatch<React.SetStateAction<string>>;
  toggleCategory: (id: string) => void;
  isCategoryChecked: (id: string) => boolean;
  toggleAvailability: (key: TopServicesAvailabilityDay) => void;
  visibleCategoryOptions: ServiceCategoryFilterOption[];
  visibleAvailabilityOptions: typeof TOP_SERVICES_AVAILABILITY_OPTIONS;
  sectionVisibility: ReturnType<typeof getFilterSectionVisibility>;
  onClose: () => void;
  onReset: () => void;
  onApply: () => void;
}

function AnalyticsServicesFilterPanel({
  activeCount,
  draft,
  setDraft,
  expandedSection,
  toggleSection,
  categorySearch,
  setCategorySearch,
  availabilitySearch,
  setAvailabilitySearch,
  toggleCategory,
  isCategoryChecked,
  toggleAvailability,
  visibleCategoryOptions,
  visibleAvailabilityOptions,
  sectionVisibility,
  onClose,
  onReset,
  onApply,
}: AnalyticsServicesFilterPanelProps) {
  const { t } = useTranslation();
  const {
    showServiceActivitySection,
    showPriceSection,
    showCategorySection,
    showAvailabilitySection,
  } = sectionVisibility;

  return (
    <>
      <div className="flex shrink-0 items-start justify-between gap-2">
        <h4 className="text-base font-semibold leading-normal text-[#171717]">
          {activeCount > 0
            ? t("analytics.common.filtersWithCount", "Filters ({{count}})", {
                count: activeCount,
              })
            : t("analytics.common.filters", "Filters")}
        </h4>
        <Button
          type="button"
          variant="span"
          className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
          onClick={onClose}
          aria-label={t("analytics.common.closeFilters", "Close filters")}
        >
          <X className="size-5" />
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
        <div className="relative shrink-0">
          <Input
            type="search"
            icon={<Search className="size-4 text-[#737373]" />}
            placeholder={t(
              "analytics.common.filterSearchPlaceholder",
              "Type a command or search",
            )}
            value={draft.panelSearch}
            onChange={(event) =>
              setDraft((previous) => ({
                ...previous,
                panelSearch: event.target.value,
              }))
            }
            className="h-9 border-[#E5E5E5] pr-9 text-sm text-[#171717] shadow-none placeholder:text-[#737373] focus-visible:border-[#E5E5E5] focus-visible:ring-0"
          />
          {draft.panelSearch ? (
            <Button
              type="button"
              variant="span"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#737373] hover:bg-zinc-100 hover:text-[#171717]"
              onClick={() =>
                setDraft((previous) => ({ ...previous, panelSearch: "" }))
              }
              aria-label={t(
                "analytics.common.clearFilterSearch",
                "Clear filter search",
              )}
            >
              <X className="size-4" />
            </Button>
          ) : null}
        </div>

        {showServiceActivitySection ? (
          <ServiceActivityFilterSection
            draft={draft}
            setDraft={setDraft}
            expandedSection={expandedSection}
            onToggleSection={toggleSection}
          />
        ) : null}

        {showPriceSection ? (
          <PriceFilterSection
            draft={draft}
            setDraft={setDraft}
            expandedSection={expandedSection}
            onToggleSection={toggleSection}
          />
        ) : null}

        {showCategorySection ? (
          <CategoryFilterSection
            categorySearch={categorySearch}
            setCategorySearch={setCategorySearch}
            visibleCategoryOptions={visibleCategoryOptions}
            toggleCategory={toggleCategory}
            isCategoryChecked={isCategoryChecked}
            expandedSection={expandedSection}
            onToggleSection={toggleSection}
          />
        ) : null}

        {showAvailabilitySection ? (
          <AvailabilityFilterSection
            draft={draft}
            availabilitySearch={availabilitySearch}
            setAvailabilitySearch={setAvailabilitySearch}
            visibleAvailabilityOptions={visibleAvailabilityOptions}
            toggleAvailability={toggleAvailability}
            expandedSection={expandedSection}
            onToggleSection={toggleSection}
          />
        ) : null}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
          onClick={onReset}
        >
          {t("analytics.common.reset", "Reset")}
        </Button>
        <Button
          type="button"
          className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm font-medium leading-normal text-[#FAFAFA] hover:bg-[#5F7597]"
          onClick={onApply}
        >
          {t("analytics.common.applyFilters", "Apply filters")}
        </Button>
      </div>
    </>
  );
}

function FilterCheckboxRow({
  checked,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  label: string;
  onCheckedChange: () => void;
}) {
  return (
    <label
      className={cn(
        "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
        checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={FILTER_CHECKBOX_CLASS}
      />
      <span className="text-sm font-normal leading-normal text-[#171717]">
        {label}
      </span>
    </label>
  );
}

function PriceRangeSlider({
  value,
  onChange,
}: {
  value: [number, number];
  onChange: (next: [number, number]) => void;
}) {
  const [minValue, maxValue] = value;
  const rangeSpan = TOP_SERVICES_PRICE_MAX - TOP_SERVICES_PRICE_MIN;
  const minPercent = ((minValue - TOP_SERVICES_PRICE_MIN) / rangeSpan) * 100;
  const maxPercent = ((maxValue - TOP_SERVICES_PRICE_MIN) / rangeSpan) * 100;

  const handleMinChange = (nextMin: number) => {
    onChange([Math.min(nextMin, maxValue), maxValue]);
  };

  const handleMaxChange = (nextMax: number) => {
    onChange([minValue, Math.max(nextMax, minValue)]);
  };

  return (
    <div className="space-y-3 px-1 py-1">
      <div className="relative mx-1 h-2 rounded-full bg-[#E5E5E5]">
        <div
          className="absolute inset-y-0 rounded-full bg-[#171717]"
          style={{
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`,
          }}
        />
        <input
          type="range"
          min={TOP_SERVICES_PRICE_MIN}
          max={TOP_SERVICES_PRICE_MAX}
          value={minValue}
          onChange={(event) => handleMinChange(Number(event.target.value))}
          className="pointer-events-none absolute inset-0 z-20 h-2 w-full appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[#171717] [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[#171717]"
        />
        <input
          type="range"
          min={TOP_SERVICES_PRICE_MIN}
          max={TOP_SERVICES_PRICE_MAX}
          value={maxValue}
          onChange={(event) => handleMaxChange(Number(event.target.value))}
          className="pointer-events-none absolute inset-0 z-30 h-2 w-full appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[#171717] [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[#171717]"
        />
      </div>
      <p className="text-center text-sm font-normal text-[#171717]">
        {formatTopServicesPriceRange(value)}
      </p>
    </div>
  );
}

export const AnalyticsServicesFilterPopover: React.FC<
  AnalyticsServicesFilterPopoverProps
> = ({ applied, onApply, categoryOptions }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<TopServicesFiltersState>(applied);
  const [expandedSection, setExpandedSection] =
    React.useState<FilterSectionId | null>(null);
  const [categorySearch, setCategorySearch] = React.useState("");
  const [availabilitySearch, setAvailabilitySearch] = React.useState("");

  const allCategoryIds = categoryOptions.map((option) => option.id);
  const activeCount = countActiveTopServicesFilters(
    draft,
    allCategoryIds.length,
  );
  const panelQuery = draft.panelSearch.trim().toLowerCase();

  const syncDraftFromApplied = React.useCallback(() => {
    setDraft(applied);
    setExpandedSection(null);
    setCategorySearch("");
    setAvailabilitySearch("");
  }, [applied]);

  const toggleSection = (section: FilterSectionId) => {
    setExpandedSection((current) => (current === section ? null : section));
  };

  const isCategoryChecked = (id: string) =>
    isCategorySelected(draft.categoryIds, allCategoryIds, id);

  const toggleCategory = (id: string) => {
    setDraft((previous) => {
      if (allCategoryIds.length === 0) {
        return previous;
      }

      const currentSelection =
        previous.categoryIds.length === 0
          ? allCategoryIds
          : previous.categoryIds;
      const has = currentSelection.includes(id);
      const nextSelection = has
        ? currentSelection.filter((item) => item !== id)
        : [...currentSelection, id];

      if (nextSelection.length === allCategoryIds.length) {
        return { ...previous, categoryIds: [] };
      }

      return { ...previous, categoryIds: nextSelection };
    });
  };

  const toggleAvailability = (key: TopServicesAvailabilityDay) => {
    setDraft((previous) => {
      if (key === "all") {
        return { ...previous, availability: ["all"] };
      }

      const withoutAll = previous.availability.filter((item) => item !== "all");
      const has = withoutAll.includes(key);
      const next = has
        ? withoutAll.filter((item) => item !== key)
        : [...withoutAll, key];

      return {
        ...previous,
        availability: next.length === 0 ? ["all"] : next,
      };
    });
  };

  const visibleCategoryOptions = categoryOptions.filter(
    (option) =>
      matchesPanelSearch(option.label, panelQuery) &&
      option.label.toLowerCase().includes(categorySearch.trim().toLowerCase()),
  );

  const visibleAvailabilityOptions = TOP_SERVICES_AVAILABILITY_OPTIONS.filter(
    (option) =>
      matchesPanelSearch(option.label, panelQuery) &&
      option.label
        .toLowerCase()
        .includes(availabilitySearch.trim().toLowerCase()),
  );

  const sectionSearchLabels = React.useMemo<FilterSectionSearchLabels>(
    () => ({
      serviceActivity: t(
        "analytics.services.filters.serviceActivity",
        "Service/ Activity",
      ),
      activities: t("analytics.services.filters.activities", "Activities"),
      services: t("analytics.services.filters.services", "Services"),
      price: t("analytics.services.filters.price", "Price"),
      category: t("analytics.services.filters.category", "Category"),
      availability: t(
        "analytics.services.filters.availability",
        "Availability",
      ),
    }),
    [t],
  );

  const sectionVisibility = getFilterSectionVisibility(
    panelQuery,
    sectionSearchLabels,
    visibleCategoryOptions.length,
    visibleAvailabilityOptions.length,
  );

  const handleReset = () => {
    setDraft(DEFAULT_TOP_SERVICES_FILTERS);
    onApply(DEFAULT_TOP_SERVICES_FILTERS);
  };

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          syncDraftFromApplied();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="h-8 min-h-8 shrink-0 gap-2 border border-slate-200 py-1 text-sm font-medium text-foreground shadow-xs"
        >
          <ListFilter className="size-4" />
          {t("analytics.common.filters", "Filters")}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex w-[320px] max-h-[min(560px,calc(100vh-120px))] flex-col gap-6 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <AnalyticsServicesFilterPanel
          activeCount={activeCount}
          draft={draft}
          setDraft={setDraft}
          expandedSection={expandedSection}
          toggleSection={toggleSection}
          categorySearch={categorySearch}
          setCategorySearch={setCategorySearch}
          availabilitySearch={availabilitySearch}
          setAvailabilitySearch={setAvailabilitySearch}
          toggleCategory={toggleCategory}
          isCategoryChecked={isCategoryChecked}
          toggleAvailability={toggleAvailability}
          visibleCategoryOptions={visibleCategoryOptions}
          visibleAvailabilityOptions={visibleAvailabilityOptions}
          sectionVisibility={sectionVisibility}
          onClose={() => setOpen(false)}
          onReset={handleReset}
          onApply={handleApply}
        />
      </PopoverContent>
    </Popover>
  );
};
